import React, { useEffect, useState } from 'react';
import { siteConfigurations } from '../shared/sites/sites';
import { getLocalizedPath } from '../shared/formatURL';
import Link from 'next/link';
import { Accordion, AccordionHeader, AccordionBody } from '@material-tailwind/react';
import { findStyleName } from '../shared/generators/generators';
import Image from 'next/image';
import { ImageIcon, Info, Palette, TriangleAlert } from 'lucide-react';
import { useGenerator } from '@/app/context/GeneratorContext';
import { useSignInModal } from '@/components/create/toggles/sign-in-modal';

export default function ImageComponent({
  action,
  translations,
  type,
  signInModalTranslations
}: {
  action: any;
  translations: any;
  type: string;
  signInModalTranslations: any;
}) {
  const [openTags, setOpenTags] = useState(1);
  const [openDescription, setOpenDescription] = useState(1);
  const [openStyle, setOpenStyle] = useState(0);
  const [openOriginal, setOpenOriginal] = useState(0);
  const [styleName, setStyleName] = useState('');
  const { generatorList, gifGeneratorList } = useGenerator();
  const searchType = type === 'image' ? 'post' : 'gif';
  const generator =
    type === 'image'
      ? generatorList.find((g) => g._id === action.post?.generator._id)
      : gifGeneratorList.find((g) => g._id === action.post?.generator._id);
  const styleImage = generator?.frontend?.styleImage;
  const chainedGif = type === 'gif' && action.post?.referenceImageId !== undefined;
  const customPrompts =
    chainedGif && action.post?.gifPrompt !== undefined
      ? action.post.gifPrompt
          .split(',')
          .map((item: string) => item.trim())
          .filter((item: string) => item !== '')
      : action.post?.customPrompt !== undefined
      ? action.post.customPrompt
          .split(',')
          .map((item: string) => item.trim())
          .filter((item: string) => item !== '')
      : [];
  const hasPrompt = chainedGif
    ? action.post?.gifPrompt !== undefined &&
      action.post?.gifPrompt !== '' &&
      action.post?.gifPrompt !== ','
    : action.post?.customPrompt !== undefined &&
      action.post?.customPrompt !== '' &&
      action.post?.customPrompt !== ',';

  const [tagId, setTagId] = useState('');
  const [tagName, setTagName] = useState('');
  const { SignInModal, setShowSignInModal } = useSignInModal(
    signInModalTranslations,
    tagId,
    tagName,
    action.post?.generator?.shortName
  );
  const handleOpenTags = (value: any) => {
    setOpenTags(openTags === value ? 0 : value);
  };
  const handleOpenDescription = (value: any) => {
    setOpenDescription(openDescription === value ? 0 : value);
  };
  const handleOpenStyle = (value: any) => {
    setOpenStyle(openStyle === value ? 0 : value);
  };
  const handleOpenOriginal = (value: any) => {
    setOpenOriginal(openOriginal === value ? 0 : value);
  };

  useEffect(() => {
    const fallback = setTimeout(() => setStyleName(action.post?.generator?.shortName), 4000);

    const interval = setInterval(() => {
      const style = findStyleName(translations.key, action.post?.generator?.shortName);
      if (style) {
        clearTimeout(fallback);
        clearInterval(interval);
        setStyleName(style);
      }
    }, 100);

    return () => {
      clearTimeout(fallback);
      clearInterval(interval);
    };
  }, [action.post?.generator?.shortName, translations.key]);

  // const customPrompts = action.post.customPrompt
  //   .split(",")
  //   .map((item: string) => item.trim())
  //   .filter((item: string) => item !== "");

  const localizedPostPath = getLocalizedPath('post');

  function manipulateString(input: string): string {
    let str = input;
    str = str
      .replace(/[\[\]\(\):.\d{}<>]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/,([^ ])/g, ', $1');
    if (str.endsWith(',')) {
      str = str.slice(0, -1);
    }
    if (str.endsWith(', ')) {
      str = str.slice(0, -2);
    }
    return str;
  }

  const isDevEnv =
    process.env.NEXT_PUBLIC_BASE_URL === 'http://localhost:3000' ||
    process.env.NEXT_PUBLIC_BASE_URL === 'https://www.8porn.ai';
  const postOrigin = siteConfigurations[action.post?.origin]?.siteUrl;
  const matchingOrigin = postOrigin === process.env.NEXT_PUBLIC_BASE_URL;
  const shouldRender = isDevEnv || matchingOrigin;
  const isImage2Gif = action.post?.referenceImageId;
  const originalImageUrl = action.post?.thumbnailUrl;
  // @ts-ignore
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`${id === open ? 'rotate-180' : ''} h-5 w-5 transition-transform`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    );
  }

  return (
    <>
      <SignInModal />
      {isImage2Gif && shouldRender && (
        <div className="mb-auto w-auto border-l-[0.5px] border-r-[0.5px] border-[#3C3C3C] bg-[#2B2D31] shadow-md ">
          <div className="flex flex-wrap pb-2 text-sm text-white">
            <Accordion
              open={openOriginal === 1}
              icon={<Icon id={1} open={openOriginal} />}
              className=" border-t-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-2 pt-2 md:px-4"
            >
              <AccordionHeader
                onClick={() => handleOpenOriginal(1)}
                className={`border-b-0 px-2 pb-2 pt-2 text-sm text-white md:px-0 ${
                  openTags === 1 ? 'text-white hover:!text-white' : ''
                }`}
              >
                {translations.originalImage}
              </AccordionHeader>
              <AccordionBody className="flex flex-col items-center justify-center pb-2 pt-0 font-normal text-white">
                <div className="mx-0 mb-[14px] border-[0.5px] border-[#DD0772] bg-[#343639]">
                  <Image
                    src={originalImageUrl || ''}
                    width={150}
                    height={150}
                    alt={styleName + 'Image'}
                    className="px-auto aspect-[2/3] bg-[#343639] object-cover hover:brightness-105 sm:object-contain"
                    placeholder="blur" // Enable the blur-up effect
                    blurDataURL={'https://cdn6.imgpog.com/stylePlaceholder.webp'} // The low-res placeholder image
                    unoptimized
                  />
                </div>
                <Link
                  href={
                    getLocalizedPath('post/') +
                    (typeof action.post?.referenceImageId === 'object'
                      ? action.post.referenceImageId._id
                      : action.post?.referenceImageId)
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-md border-[0.5px] border-grey-outline bg-[#343639] px-4 py-2 hover:brightness-150 "
                >
                  <ImageIcon size={16} /> <span>{translations.viewOriginal}</span>
                </Link>
              </AccordionBody>
            </Accordion>
          </div>
        </div>
      )}
      {shouldRender && (
        <div className="mb-auto w-auto border-l-[0.5px] border-r-[0.5px] border-[#3C3C3C] bg-[#2B2D31] shadow-md ">
          <div className="flex flex-wrap pb-2 text-sm text-white">
            <Accordion
              open={openStyle === 1}
              icon={<Icon id={1} open={openStyle} />}
              className=" border-t-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-2 pt-2 md:px-4"
            >
              <AccordionHeader
                onClick={() => handleOpenStyle(1)}
                className={`border-b-0 px-2 pb-2 pt-2 text-sm text-white md:px-0 ${
                  openTags === 1 ? 'text-white hover:!text-white' : ''
                }`}
              >
                {translations.style}: {styleName}
              </AccordionHeader>
              <AccordionBody className="flex flex-col items-center justify-center pb-2 pt-0 font-normal text-white">
                <button
                  type="button"
                  className="mx-0 mb-[14px] rounded-xl border-[0.5px] border-[#3C3C3C] bg-[#343639] text-sm font-medium  text-white focus:outline-none focus:ring-0 focus:ring-gray-300 "
                >
                  <Image
                    src={styleImage || ''}
                    width={150}
                    height={150}
                    alt={styleName + 'Image'}
                    className="px-auto rounded-xl bg-[#343639] object-cover hover:brightness-105 sm:object-contain"
                    placeholder="blur" // Enable the blur-up effect
                    blurDataURL={'https://cdn6.imgpog.com/stylePlaceholder.webp'} // The low-res placeholder image
                    unoptimized
                  />
                  <div className="mt-1 truncate">{styleName}</div>
                </button>
                <Link
                  href={
                    type === 'image'
                      ? getLocalizedPath(`post/`) +
                        'search?search=&style=' +
                        action.post?.generator?.shortName
                      : getLocalizedPath(`gif/`) + 'search?search=' + styleName
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-md border-[0.5px] border-grey-outline bg-[#343639] px-4 py-2 hover:brightness-150 "
                >
                  <Palette size={16} /> <span>{translations.viewStyle}</span>
                </Link>
              </AccordionBody>
            </Accordion>
          </div>
        </div>
      )}
      {shouldRender && (
        <div className="mb-auto w-auto border-b-[0.5px] border-l-[0.5px] border-r-[0.5px] border-[#3C3C3C] bg-[#2B2D31] shadow-md ">
          <div className="flex flex-wrap pb-2 text-sm text-white">
            {type === 'image' && action.tagStringArray.length > 0 ? (
              // POST
              <>
                <Accordion
                  open={openTags === 1}
                  icon={<Icon id={1} open={openTags} />}
                  className=" border-t-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-2 pt-2 md:px-4"
                >
                  <AccordionHeader
                    onClick={() => handleOpenTags(1)}
                    className={`border-b-0 px-2 pb-2 pt-2 text-sm text-white md:px-0 ${
                      openTags === 1 ? 'text-white hover:!text-white' : ''
                    }`}
                  >
                    {translations.tags}
                  </AccordionHeader>
                  <AccordionBody className="pb-2 pt-2 font-normal text-white">
                    <div className="flex flex-row flex-wrap items-center">
                      {action.tagStringArray?.map((tag: any, index: any) => (
                        <div
                          key={index}
                          className={`mb-2 mr-2 flex items-center rounded-full border-[0.5px] border-[#3C3C3C] bg-[#343639] px-3.5 py-[6px] text-sm font-medium text-white hover:brightness-110 sm:pl-5 sm:pr-5 ${
                            tag.ugc ? 'border border-ugc-tags ' : ''
                          }`}
                        >
                          <Link
                            prefetch={false}
                            href={`${localizedPostPath}/search?search=${encodeURIComponent(
                              tag.tagName
                            )}`}
                            className="flex items-center"
                          >
                            <div className="my-auto">{tag.tagName}</div>
                            <div className="border-1 my-auto ml-[8px] h-3 border-r border-white"></div>
                          </Link>

                          <div
                            aria-label="Top Posts Using This Tag"
                            className="w-auto cursor-pointer pl-[8px]"
                            onClick={() => {
                              setTagId(action.tagIds[index]);
                              setTagName(tag.tagName);
                              setShowSignInModal(true);
                            }}
                          >
                            <Info color="white" className="my-auto h-5 w-auto" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionBody>
                </Accordion>
              </>
            ) : action.tagStringArray?.length > 0 ? (
              // GIF
              <>
                <Accordion
                  open={openTags === 1}
                  icon={<Icon id={1} open={openTags} />}
                  className="border-t-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-2 pt-2 md:px-4"
                >
                  <AccordionHeader
                    onClick={() => handleOpenTags(1)}
                    className={`border-b-0 px-2 pb-2 pt-2 text-sm text-white md:px-0 ${
                      openTags === 1 ? 'text-white hover:!text-white' : ''
                    }`}
                  >
                    {translations.tags}
                  </AccordionHeader>
                  <AccordionBody className="pb-2 pt-2 font-normal text-white">
                    <div className="flex flex-row flex-wrap items-center">
                      {action.tagStringArray?.map((tag: any, index: any) => (
                        <div
                          key={index}
                          className={`mb-2 mr-2 flex items-center rounded-full border-[0.5px] border-[#3C3C3C] bg-[#343639] px-3.5 py-[6px] text-sm font-medium text-white hover:brightness-110 sm:pl-5 sm:pr-5 ${
                            tag.ugc ? 'border border-ugc-tags ' : ''
                          }`}
                        >
                          <Link
                            prefetch={false}
                            href={`${localizedPostPath}/search?search=${encodeURIComponent(
                              tag.tagName
                            )}`}
                            className="flex items-center"
                          >
                            <div className="my-auto">{tag.tagName}</div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </AccordionBody>
                </Accordion>
              </>
            ) : (
              <>
                <div className="flex w-full items-center justify-start border-t-[0.5px] border-[#3C3C3C] px-4 pb-1 pt-3 text-sm font-medium text-white">
                  {translations.tagsNotUsed}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {hasPrompt && shouldRender ? (
        <div className=" w-auto border-b-[0.5px] border-l-[0.5px] border-r-[0.5px] border-[#3C3C3C] bg-[#2B2D31] shadow-md sm:rounded-bl-md sm:rounded-br-md">
          <div className="text-sm font-medium text-white">
            {/* {`${action.post.customPrompt}`.replace(/,([^,]*)$/, "$1")} */}
            <Accordion
              open={openDescription === 1}
              icon={<Icon id={1} open={openDescription} />}
              className=" bg-[#2B2D31] px-2 pt-2 sm:rounded-bl-md sm:rounded-br-md md:px-4"
            >
              <AccordionHeader
                onClick={() => handleOpenDescription(1)}
                className={`border-b-0 px-[4px] pb-4 pt-2 text-sm text-white sm:px-0 ${
                  openTags === 1 ? 'text-white hover:!text-white' : ''
                }`}
              >
                {translations.useOfCustomPrompt}
              </AccordionHeader>
              <AccordionBody className="px-[4px] pb-4 pt-0 font-normal text-white sm:px-0">
                {customPrompts.map((prompt: string, index: number) => (
                  <Link
                    prefetch={false}
                    key={index}
                    href={`/${searchType}/search?search=${encodeURIComponent(
                      manipulateString(prompt)
                    )}`}
                    // className="inline-block"
                  >
                    {index === customPrompts.length - 1
                      ? prompt.replace(/,([^,]*)$/, '$1')
                      : prompt.replace(/,([^,]*)$/, '$1') + ', '}
                  </Link>
                ))}
              </AccordionBody>
            </Accordion>
            <div>
              <></>
            </div>
          </div>
        </div>
      ) : (
        shouldRender && (
          <div className="w-auto border-b-[0.5px] border-l-[0.5px]  border-r-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-4 py-3 text-sm font-medium text-white shadow-md sm:rounded-bl-md sm:rounded-br-md">
            {translations.promptNotUsed}
          </div>
        )
      )}
      <div className="mt-[20px] w-auto border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] shadow-md sm:rounded-md">
        <div className="inline-flex items-center justify-center gap-1 px-5 pt-4 text-sm font-medium text-white">
          <TriangleAlert size={14} /> <span>{translations.AIPornDisclaimer}</span>
        </div>
        <div className="px-5 pt-4 text-sm font-medium text-white">
          {translations.AIPornDisclaimerText1}{' '}
        </div>
        <div className="px-5 pb-4 text-sm font-medium text-white">
          <a
            href={`https://createaiart.zendesk.com/hc/en-us/articles/30361023844379-Reporting-Harmful-Content`}
            className="underline"
            target="_blank"
          >
            {translations.AIPornDisclaimerText2}
          </a>
        </div>
      </div>
    </>
  );
}
